import { useMemo } from "react";

import { createSelector } from "@reduxjs/toolkit";

import { selectEnableOrganizationPlansPermission } from "Reducers/featureFlags/featureFlags.selectors";
import { orgByDescriptionFieldSelector } from "Reducers/organization/organization";
import { useAppSelector } from "Store/hooks";

import type { OrganizationMemberPermissions } from "Api/organizations/member/Member.types";
import type { RootState } from "Store/configureStore";

export const permissions = {
  admin: "admin",
  billing: "billing",
  plans: "plans",
  members: "members",
  "projects:create": "projects:create",
  "projects:list": "projects:list"
} as const;

export type Permission = OrganizationMemberPermissions;

// This function is not connected to the redux store and should be used when we
// are sure that having all the permissions is not a problem and they can be
// filtered later on.
export const generateStaticPermissionsMap = <T>(initialValue: T) =>
  Object.fromEntries(
    Object.keys(permissions).map(permission => [permission, initialValue])
  ) as { [key in Permission]: T };

/** Whether the organization is on the new billing system. Fails open to
 * legacy (false) when the organization isn't loaded yet. */
export const isNewBillingSystemSelector = createSelector(
  [
    orgByDescriptionFieldSelector,
    (_: RootState, organizationNameOrId: string) => organizationNameOrId
  ],
  (organizations, nameOrId) =>
    Object.values(organizations).find(
      o => o?.name === nameOrId || o?.id === nameOrId
    )?.billing_legacy === false
);

/** Permission keys that can be granted on the given organization: `plans`
 * requires its feature flag, and `billing` no longer applies on the new
 * billing system, where billing is managed at the billing-account level. */
export const assignablePermissionKeysSelector = createSelector(
  [selectEnableOrganizationPlansPermission, isNewBillingSystemSelector],
  (isPlansEnabled, isNewBillingSystem) =>
    (Object.keys(permissions) as Array<Permission>).filter(p => {
      if (!isPlansEnabled && p === permissions.plans) return false;
      if (isNewBillingSystem && p === permissions.billing) return false;
      return true;
    })
);

export const useAvailablePermissionsKeys = () => {
  const isPlansEnabled = useAppSelector(
    selectEnableOrganizationPlansPermission
  );

  const fitleredPermissions = useMemo(
    () =>
      Object.keys(permissions).filter(p => {
        if (!isPlansEnabled && p === permissions.plans) return false;
        return true;
      }) as Array<Permission>,
    [isPlansEnabled]
  );

  return [fitleredPermissions] as const;
};
